import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function cardLabel(pm) {
  const brand = pm?.brand ? String(pm.brand).toUpperCase() : "CARTE";
  const last4 = pm?.last4 ? `•••• ${pm.last4}` : "";
  const exp =
    pm?.exp_month && pm?.exp_year ? ` — ${pm.exp_month}/${String(pm.exp_year).slice(-2)}` : "";
  return `${brand} ${last4}${exp}`.trim();
}

/**
 * Choix de carte enregistrée avant achat dossier (si plusieurs cartes Stripe).
 */
export default function BuyPaymentMethodPicker({ value, onChange, className }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/payment-methods/list", { params: { light: 1 } });
        const list = Array.isArray(res?.data?.payment_methods) ? res.data.payment_methods : [];
        if (cancelled) return;
        setItems(list);
        if (list.length >= 1) {
          const def = list.find((p) => p.is_default) || list[0];
          if (def?.id) onChange?.(def.id);
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init carte par défaut une fois
  }, []);

  if (loading) {
    return <p className={cn("text-xs text-slate-500", className)}>Chargement de vos cartes…</p>;
  }

  if (items.length <= 1) return null;

  return (
    <div
      className={cn("rounded-xl border border-slate-200 bg-white p-4 space-y-3", className)}
      data-testid="buy-payment-method-picker"
    >
      <p className="text-sm font-semibold text-slate-900">Carte pour cet achat</p>
      <p className="text-xs text-slate-600">
        Plusieurs cartes sont enregistrées : choisissez celle à utiliser pour ce paiement (séquestre).
      </p>
      <ul className="space-y-2" role="radiogroup" aria-label="Carte pour cet achat">
        {items.map((pm) => (
          <li key={pm.id}>
            <label
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                value === pm.id
                  ? "border-[#2D7FF9] bg-sky-50/80 ring-1 ring-[#2D7FF9]/30"
                  : "border-slate-200 hover:border-slate-300",
              )}
            >
              <input
                type="radio"
                name="buy-payment-method"
                className="h-4 w-4 shrink-0"
                checked={value === pm.id}
                onChange={() => onChange?.(pm.id)}
                data-testid={`buy-pm-${pm.id}`}
              />
              <span className="min-w-0 flex-1 font-medium text-slate-800">{cardLabel(pm)}</span>
              {pm.is_default ? (
                <span className="shrink-0 text-[10px] font-semibold uppercase text-slate-500">Défaut</span>
              ) : null}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
